import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ArbiterStatus } from '@/types/entities';

interface ArbiterMessage {
  id: string;
  role: 'user' | 'arbiter';
  content: string;
  timestamp: string;
  context?: {
    pageRoute?: string;
    relatedIndicatorId?: string;
    relatedRiskId?: string;
  };
}

interface ArbiterState {
  panelOpen: boolean;
  status: ArbiterStatus;
  activeChallenges: number;
  messages: ArbiterMessage[];
  isTyping: boolean;

  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  setStatus: (status: ArbiterStatus) => void;
  setActiveChallenges: (count: number) => void;
  addMessage: (message: ArbiterMessage) => void;
  clearMessages: () => void;
  setTyping: (isTyping: boolean) => void;
  sendMessage: (content: string, context?: ArbiterMessage['context']) => void;
}

// Template-based response generator for BRC context (no LLM API)
const generateResponse = (userContent: string, context?: ArbiterMessage['context']): string => {
  const lowerContent = userContent.toLowerCase();

  // Context-aware responses
  if (context?.relatedIndicatorId) {
    return `I can help you analyze this indicator. Based on the current data, I recommend reviewing the trend analysis and checking if any threshold breaches require escalation to the Board.`;
  }

  if (context?.relatedRiskId) {
    return `For this risk, I suggest reviewing the linked KRIs and their appetite alignment. Consider whether the current headroom is sufficient under adverse scenarios.`;
  }

  
  // Banking-specific keyword responses (UK Bank plc overlay)
  if (lowerContent.includes('pra') || lowerContent.includes('fca') || lowerContent.includes('basel')) {
    return `UK Bank plc operates under PRA and FCA dual-regulation. The Basel III.1 framework (implemented via CRR3) sets capital floors, output floors, and revised standardised approaches. I can help you trace regulatory requirements to specific controls and risk appetite statements.`;
  }

  if (lowerContent.includes('icaap') || lowerContent.includes('ilaap')) {
    return `The ICAAP and ILAAP processes are central to UK Bank plc's Pillar 2 framework. ICAAP assesses internal capital adequacy against stress scenarios, while ILAAP evaluates liquidity resilience. Both feed into the PRA SREP assessment and Total Capital Requirement.`;
  }

  if (lowerContent.includes('ifrs9') || lowerContent.includes('ifrs 9') || lowerContent.includes('ecl') || lowerContent.includes('expected credit loss')) {
    return `IFRS 9 Expected Credit Loss (ECL) provisioning uses a three-stage model: Stage 1 (12-month ECL), Stage 2 (lifetime ECL on significant credit deterioration), and Stage 3 (credit-impaired). UK Bank plc models incorporate forward-looking macroeconomic scenarios weighted by probability.`;
  }

  // Keyword-based responses
  if (lowerContent.includes('risk appetite') || lowerContent.includes('headroom')) {
    return `The Risk Appetite framework tracks ${Math.floor(Math.random() * 30) + 50} statements across all risk categories. I can help you understand headroom calculations or identify appetite breaches requiring Board attention.`;
  }

  if (lowerContent.includes('scenario') || lowerContent.includes('stress')) {
    return `Scenario analysis covers Base, Adverse, and Severe scenarios with impact projections across ${Math.floor(Math.random() * 20) + 40} KRIs. I can help you interpret stress test results or review resilience metrics.`;
  }

  if (lowerContent.includes('kri') || lowerContent.includes('indicator')) {
    return `The system monitors ${Math.floor(Math.random() * 50) + 100} indicators (KRIs, KCIs, KPIs, EWIs). I can help you understand RAG status, forecast trends, or review trust scores.`;
  }

  if (lowerContent.includes('control') || lowerContent.includes('effectiveness')) {
    return `Control effectiveness is tracked with trust scores and evidence-based ratings. I can help you identify controls requiring attention or review the three lines of defense coverage.`;
  }

  if (lowerContent.includes('culture') || lowerContent.includes('conduct')) {
    return `Culture and conduct metrics cover ${Math.floor(Math.random() * 15) + 25} indicators across behavior, speaking up, and consequence management. I can help you interpret survey results or identify emerging themes.`;
  }

  if (lowerContent.includes('action') || lowerContent.includes('remediation')) {
    return `I've identified ${Math.floor(Math.random() * 10) + 5} active remediation actions. Would you like me to prioritize them by risk severity or show overdue milestones?`;
  }

  if (lowerContent.includes('help') || lowerContent.includes('how')) {
    return `I'm here to guide you through the Board Risk Committee Control Centre. You can ask me about risk appetite, KRIs, scenario analysis, control effectiveness, culture metrics, or any board reporting needs. What would you like to explore?`;
  }

  if (lowerContent.includes('trust') || lowerContent.includes('data quality')) {
    return `Trust scores combine data quality, model reliability, and process adherence. I can help you review indicators with low trust scores or understand the methodology.`;
  }

  if (lowerContent.includes('forecast') || lowerContent.includes('ml')) {
    return `ML forecasting provides 30/60/90-day projections with confidence bands. I can help you interpret model predictions or review feature contributions for key indicators.`;
  }

  // Default response
  return `I understand you're asking about "${userContent}". Could you provide more context or specify which area you'd like to explore: Risk Appetite, KRIs, Scenario Analysis, Controls, Culture & Conduct, or Board Reporting?`;
};

export const useArbiterStore = create<ArbiterState>()(
  persist(
    (set) => ({
      panelOpen: false,
      status: 'IDLE',
      activeChallenges: 0,
      messages: [],
      isTyping: false,

      setPanelOpen: (open) => set({ panelOpen: open }),
      togglePanel: () => set((state) => ({ panelOpen: !state.panelOpen })),
      openPanel: () => set({ panelOpen: true }),
      closePanel: () => set({ panelOpen: false }),
      setStatus: (status) => set({ status }),
      setActiveChallenges: (count) => set({ activeChallenges: count }),

      addMessage: (message) =>
        set((state) => ({
          messages: [...state.messages, message],
        })),

      clearMessages: () => set({ messages: [] }),

      setTyping: (isTyping) => set({ isTyping }),

      sendMessage: (content, context) => {
        const userMessage: ArbiterMessage = {
          id: `msg-${Date.now()}-user`,
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
          context,
        };

        set((state) => ({
          messages: [...state.messages, userMessage],
          isTyping: true,
        }));

        // Simulate Arbiter response (template-based)
        setTimeout(() => {
          const arbiterMessage: ArbiterMessage = {
            id: `msg-${Date.now()}-arbiter`,
            role: 'arbiter',
            content: generateResponse(content, context),
            timestamp: new Date().toISOString(),
          };

          set((state) => ({
            messages: [...state.messages, arbiterMessage],
            isTyping: false,
          }));
        }, 1500);
      },
    }),
    {
      name: 'riskmetrica-brc-arbiter',
      partialize: (state) => ({
        messages: state.messages.slice(-50), // Keep last 50 messages
      }),
    }
  )
);
